import axios from 'axios'
import {
    PartnerAppointmentDetail,
    PartnerAppointmentFilters,
    PartnerAppointmentsResponse,
    PartnerInfo,
    PartnerLoginResponse,
} from './partner-portal.types'

const PARTNER_TOKEN_KEY = 'partner_portal_token'
const PARTNER_DATA_KEY = 'partner_portal_data'
const COMPANY_LOGO_KEY = 'partner_portal_company_logo'
const PARTNER_LOGO_KEY = 'partner_portal_partner_logo'

function getPartnerClient() {
    const baseURL = import.meta.env.VITE_API_BASE_URL

    const client = axios.create({
        baseURL,
        headers: {
            'Content-Type': 'application/json',
        },
        timeout: 30000,
    })

    client.interceptors.request.use(config => {
        const token = sessionStorage.getItem(PARTNER_TOKEN_KEY)
        if (token) {
            config.headers.Authorization = `Bearer ${token}`
        }
        return config
    })

    client.interceptors.response.use(
        response => response,
        error => {
            if (error.response?.status === 401) {
                sessionStorage.removeItem(PARTNER_TOKEN_KEY)
                sessionStorage.removeItem(PARTNER_DATA_KEY)
                sessionStorage.removeItem(PARTNER_LOGO_KEY)
                sessionStorage.removeItem(COMPANY_LOGO_KEY)
                window.location.href = '/person/partners'
            }
            return Promise.reject(error)
        }
    )

    return client
}

export function getPartnerToken(): string | null {
    return sessionStorage.getItem(PARTNER_TOKEN_KEY)
}

export function getPartnerData(): PartnerLoginResponse['partner'] | null {
    try {
        const data = sessionStorage.getItem(PARTNER_DATA_KEY)
        return data ? JSON.parse(data) : null
    } catch {
        return null
    }
}

export function getPartnerLogoUrl(): string | null {
    return sessionStorage.getItem(PARTNER_LOGO_KEY)
}

export function getCompanyLogoUrl(): string | null {
    return sessionStorage.getItem(COMPANY_LOGO_KEY)
}

export function clearPartnerSession() {
    sessionStorage.removeItem(PARTNER_TOKEN_KEY)
    sessionStorage.removeItem(PARTNER_DATA_KEY)
    sessionStorage.removeItem(PARTNER_LOGO_KEY)
    sessionStorage.removeItem(COMPANY_LOGO_KEY)
}

export async function partnerLogin(
    accessCode: string,
    turnstileToken: string
): Promise<{ success: boolean; result?: PartnerLoginResponse; error?: string }> {
    try {
        const baseURL = import.meta.env.VITE_API_BASE_URL
        const response = await axios.post<PartnerLoginResponse>(
            `${baseURL}/auth/partner/login`,
            { accessCode, turnstileToken }
        )

        sessionStorage.setItem(PARTNER_TOKEN_KEY, response.data.access_token)
        sessionStorage.setItem(PARTNER_DATA_KEY, JSON.stringify(response.data.partner))
        if (response.data.partner.logoUrl) {
            sessionStorage.setItem(PARTNER_LOGO_KEY, response.data.partner.logoUrl)
        }
        if (response.data.companyLogoUrl) {
            sessionStorage.setItem(COMPANY_LOGO_KEY, response.data.companyLogoUrl)
        }

        return { success: true, result: response.data }
    } catch (error: any) {

        const messages = error?.response?.data?.message
        let message = ''

        if (messages && Array.isArray(messages)) {
            message = messages.join(', ')
        }
        else if (messages) {
            message = messages
        }
        else {
            message = 'Erro ao realizar login'
        }

        return { success: false, error: message }
    }
}

export async function partnerForgotCode(
    email: string,
    turnstileToken: string
): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
        const baseURL = import.meta.env.VITE_API_BASE_URL
        const response = await axios.post(
            `${baseURL}/auth/partner/forgot-code`,
            { email, turnstileToken }
        )

        return { success: true, message: response.data.message }
    } catch (error: any) {
        const message =
            error?.response?.data?.message || 'Erro ao enviar e-mail'
        return { success: false, error: message }
    }
}

export async function getPartnerAppointments(
    filters: PartnerAppointmentFilters
): Promise<{ success: boolean; result?: PartnerAppointmentsResponse; error?: string }> {
    try {
        const client = getPartnerClient()
        const response = await client.get<PartnerAppointmentsResponse>(
            '/partner-portal/appointments',
            { params: filters }
        )

        return { success: true, result: response.data }
    } catch (error: any) {
        const message =
            error?.response?.data?.message || 'Erro ao buscar agendamentos'
        return { success: false, error: message }
    }
}

export async function getPartnerAppointmentDetail(
    type: string,
    id: number
): Promise<{ success: boolean; result?: PartnerAppointmentDetail; error?: string }> {
    try {
        const client = getPartnerClient()
        const response = await client.get<PartnerAppointmentDetail>(
            `/partner-portal/appointments/${type}/${id}`
        )

        return { success: true, result: response.data }
    } catch (error: any) {
        const message =
            error?.response?.data?.message || 'Erro ao buscar detalhes do agendamento'
        return { success: false, error: message }
    }
}

export async function getPartnerInfo(): Promise<{
    success: boolean
    result?: PartnerInfo
    error?: string
}> {
    try {
        const client = getPartnerClient()
        const response = await client.get<PartnerInfo>('/partner-portal/me')

        return { success: true, result: response.data }
    } catch (error: any) {
        const message =
            error?.response?.data?.message || 'Erro ao buscar informações'
        return { success: false, error: message }
    }
}
